import type React from "react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../utils/api";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  // Capture the single-use token once, then scrub it from the address bar /
  // history so it doesn't linger. useState initializer runs on first render.
  const [token] = useState(() => {
    const t = searchParams.get("token");
    if (t) window.history.replaceState({}, "", "/reset-password");
    return t;
  });

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  // A 400 means the token itself is bad (invalid/expired/used) — show the
  // same dead-link state as a missing token instead of an inline error.
  const [tokenDead, setTokenDead] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (password.length < 8) {
      return setError("Password must be at least 8 characters.");
    }
    if (password.length > 72 || new TextEncoder().encode(password).length > 72) {
      return setError("Password must be 72 characters or fewer.");
    }
    if (password !== confirm) {
      return setError("Passwords don't match.");
    }
    setLoading(true);
    try {
      await api("/api/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token, newPassword: password }),
      });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setTokenDead(true);
      } else if (err instanceof ApiError && err.status === 422) {
        setError(err.message);
      } else if (err instanceof ApiError && err.status === 429) {
        setError("Too many requests. Wait a few minutes, then try again.");
      } else {
        setError("Couldn't reset your password. Try again shortly.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token || tokenDead) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.brandBadge}>StintSense</div>
          <div className="eyebrow" style={{ marginBottom: "0.75rem" }}>
            Account recovery
          </div>
          <h2 style={styles.title}>This link won't work</h2>
          <p style={styles.copy}>
            This reset link is invalid or has expired. Request a new one and try
            again.
          </p>
          <div style={{ marginTop: "1.25rem" }}>
            <Link to="/forgot-password" className="btn btn-primary" style={{ textDecoration: "none" }}>
              Request a new link
            </Link>
          </div>
          <div style={styles.footerNote}>
            <Link to="/login" style={styles.footerLink}>
              ← Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div style={styles.page}>
        <div style={styles.card} aria-live="polite">
          <div style={styles.brandBadge}>StintSense</div>
          <div style={styles.doneMark}>✓</div>
          <h2 style={styles.title}>Password reset</h2>
          <p style={styles.copy}>
            Your password's been reset and you've been signed out on all
            devices. Sign in with your new password.
          </p>
          <div style={{ marginTop: "1.25rem" }}>
            <Link to="/login" className="btn btn-primary" style={{ textDecoration: "none" }}>
              Go to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.brandBadge}>StintSense</div>
        <div style={styles.header}>
          <div className="eyebrow" style={{ marginBottom: "0.75rem" }}>
            Account recovery
          </div>
          <h2 style={styles.title}>Choose a new password</h2>
          <p style={styles.copy}>
            Set a new password for your account. It needs at least 8 characters.
          </p>
        </div>

        <form onSubmit={handleSubmit} style={styles.formBody}>
          <label style={styles.field}>
            <span style={styles.fieldLabel}>New password</span>
            <input
              className="input"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="At least 8 characters"
              autoComplete="new-password"
              autoFocus
            />
          </label>
          <label style={styles.field}>
            <span style={styles.fieldLabel}>Confirm new password</span>
            <input
              className="input"
              type="password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              placeholder="Enter it again"
              autoComplete="new-password"
            />
          </label>

          {error && <div style={styles.errorBanner} role="alert">{error}</div>}

          <button type="submit" className="btn btn-primary" disabled={loading} style={styles.submitButton}>
            {loading ? "Resetting…" : "Reset password"}
          </button>
        </form>

        <div style={styles.footerNote}>
          <Link to="/login" style={styles.footerLink}>
            ← Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1.25rem",
  },
  card: {
    width: "min(27rem, 100%)",
    borderRadius: "var(--r-md)",
    border: "1px solid var(--rule)",
    background: "var(--ink-2)",
    boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.025)",
    padding: "2rem 1.75rem",
    display: "flex",
    flexDirection: "column",
  },
  brandBadge: {
    display: "inline-flex",
    alignItems: "center",
    width: "fit-content",
    padding: "0.4rem 0.85rem",
    borderRadius: "999px",
    border: "1px solid var(--live-edge)",
    background: "var(--live-soft)",
    color: "var(--live-strong)",
    fontFamily: "var(--font-body)",
    fontSize: "0.8125rem",
    fontWeight: 500,
    marginBottom: "1.5rem",
  },
  header: {
    marginBottom: "1.25rem",
  },
  title: {
    fontSize: "1.75rem",
    fontWeight: 700,
    marginBottom: "0.45rem",
    color: "var(--text-strong)",
  },
  copy: {
    color: "var(--text-secondary)",
    fontSize: "0.98rem",
    lineHeight: 1.7,
  },
  formBody: {
    display: "flex",
    flexDirection: "column",
    gap: "0.95rem",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "0.45rem",
  },
  fieldLabel: {
    color: "var(--text-secondary)",
    fontFamily: "var(--font-body)",
    fontSize: "0.85rem",
    fontWeight: 500,
  },
  errorBanner: {
    padding: "0.85rem 1rem",
    borderRadius: "var(--r-sm)",
    border: "1px solid rgba(248, 113, 113, 0.28)",
    background: "var(--flag-red-soft)",
    color: "var(--flag-red)",
    fontWeight: 600,
  },
  submitButton: {
    width: "100%",
    marginTop: "0.25rem",
  },
  footerNote: {
    marginTop: "1rem",
    paddingTop: "1rem",
    borderTop: "1px solid var(--rule)",
    color: "var(--text-muted)",
    fontSize: "0.9rem",
    lineHeight: 1.6,
  },
  footerLink: {
    color: "var(--live-strong)",
    fontWeight: 600,
    textDecoration: "none",
  },
  doneMark: {
    width: "3.5rem",
    height: "3.5rem",
    borderRadius: "999px",
    display: "grid",
    placeItems: "center",
    fontSize: "1.6rem",
    color: "var(--flag-green)",
    border: "1px solid var(--flag-green)",
    background: "var(--flag-green-soft, rgba(74, 222, 128, 0.12))",
    marginBottom: "1rem",
  },
};
